import { useState } from 'react'

import { useResourcesApi } from '@/services/api/resourcesApi'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import {
    Modality,
    OptionsField,
    OptionsPrograms,
    AcademicCycleWithInitialDate
} from '@/types/forms/'

// Get modality category from env by modalityId
function getModalityCategoryFromEnv(
    modalityId: number | string
): string | undefined {
    return import.meta.env[`VITE_MODALITY_CATEGORY_${modalityId}`]
}

// Helper function to format jornadaId to 3 digits with leading zeros
function formatJornadaId(jornadaId: number | string): string {
    const num =
        typeof jornadaId === 'string' ? parseInt(jornadaId, 10) : jornadaId

    if (isNaN(num)) {
        return String(jornadaId).padStart(3, '0')
    }

    return String(num).padStart(3, '0')
}

interface AcademicAdmissionFunctionsProps {
    fetchPrograms: (
        modalityId: string
    ) => Promise<OptionsPrograms[] | undefined>
    fetchAdmissionTypes: (
        programId: string
    ) => Promise<OptionsField[] | undefined>
    fetchCampusByProgram: (
        programId: string,
        admissionTypeId: string
    ) => Promise<OptionsField[] | undefined>
    fetchJornada: (
        programId: string,
        admissionTypeId: string,
        campusId: string
    ) => Promise<OptionsField[] | undefined>
    fetchAcademicCycleWithInitialDate: (
        programId: number,
        admissionTypeId: string,
        headquarterId: number,
        jornadaId: number
    ) => Promise<AcademicCycleWithInitialDate[] | undefined>
    fetchModality: (
        programId: string,
        admissionTypeId: string,
        jornadaId: string
    ) => Promise<Modality | undefined>
    isLoading: boolean
    isLoadingAdmissionTypes: boolean
    isLoadingCampus: boolean
    isLoadingJornada: boolean
    isLoadingCycle: boolean
    colorChip: (
        modalityName: string
    ) => 'warning' | 'primary' | 'secondary' | 'default' | 'success' | 'danger'
}

// eslint-disable-next-line max-lines-per-function
export function AcademicAdmissionFunctions(): AcademicAdmissionFunctionsProps {
    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY, //TODO: errors and info only instead all, on default config
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isLoadingAdmissionTypes, setIsLoadingAdmissionTypes] =
        useState<boolean>(false)
    const [isLoadingCampus, setIsLoadingCampus] = useState<boolean>(false)
    const [isLoadingJornada, setIsLoadingJornada] = useState<boolean>(false)
    const [isLoadingCycle, setIsLoadingCycle] = useState<boolean>(false)

    // Mapeo de modalitiesFinalId a tipo de programa
    const getProgramTypeByModalityId = (
        modalitiesFinalId: number | undefined
    ): 'pregrado' | 'especializacion' | 'maestria' | 'doctorado' | null => {
        if (!modalitiesFinalId) {
            return null
        }

        // Mapeo basado en los IDs de modalidad conocidos
        // Modalidad 1: Especialización
        // Modalidad 4: Especialización presencial
        // Modalidad 16: Pregrado
        // Agregar más IDs según sea necesario
        const modalityTypeMap: Record<
            number,
            'pregrado' | 'especializacion' | 'maestria' | 'doctorado'
        > = {
            1: 'especializacion',
            4: 'especializacion',
            16: 'pregrado'
            // Agregar más mapeos según sea necesario
        }

        return modalityTypeMap[modalitiesFinalId] || null
    }

    const getProgramType = (
        planName: string,
        modalitiesFinalId?: number
    ): 'pregrado' | 'especializacion' | 'maestria' | 'doctorado' => {
        // Primero intentar usar el modalitiesFinalId si está disponible
        const typeById = getProgramTypeByModalityId(modalitiesFinalId)

        if (typeById) {
            return typeById
        }

        // Si no hay ID o no está mapeado, usar el nombre como fallback
        const modalityLower = planName.toLowerCase().trim()

        if (
            modalityLower.includes('maestría') ||
            modalityLower.includes('maestria') ||
            planName.toUpperCase().includes('MTR.')
        ) {
            return 'maestria'
        }

        if (
            modalityLower.includes('espec.') ||
            modalityLower.includes('esp.') ||
            planName.toUpperCase().includes('ESP.') ||
            modalityLower.includes('especialización') ||
            modalityLower.includes('especializacion')
        ) {
            return 'especializacion'
        }

        if (modalityLower.includes('doctorado')) {
            return 'doctorado'
        }

        return 'pregrado'
    }

    const fetchPrograms = async (
        modalityId: string
    ): Promise<OptionsPrograms[] | undefined> => {
        if (!modalityId) {
            return undefined
        }

        setIsLoading(true)

        try {
            const response =
                await resourcesApi.getAcademicPlansByModality(modalityId)
            const { data, status } = response || {}

            if (status === 200 && Array.isArray(data) && data.length > 0) {
                const mappedOptions = data.map((program: any) => {
                    const programType = getProgramType(
                        program.planName,
                        program.modalitiesFinalId
                    )
                    const modalityNameMap: Record<string, string> = {
                        especializacion: 'Especialización',
                        maestria: 'Maestría',
                        doctorado: 'Doctorado',
                        pregrado: 'Pregrado'
                    }

                    return {
                        value: program.planId.toString(),
                        label: program.planName,
                        modalityName:
                            modalityNameMap[programType] || 'Programa',
                        programType: programType
                    }
                })

                // Filtrar programas opcionalmente por IDs permitidos desde .env
                const filterEnabled =
                    import.meta.env.VITE_FILTER_PROGRAMS_ENABLED === 'true'
                const allowedProgramIds = (import.meta.env
                    .VITE_ALLOWED_PROGRAM_IDS || '')
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean)

                const filteredOptions =
                    filterEnabled && allowedProgramIds.length > 0
                        ? mappedOptions.filter((opt) =>
                              allowedProgramIds.includes(opt.value)
                          )
                        : mappedOptions

                return filteredOptions
            }
        } catch (error) {
            console.error('Error fetching programs:', error)
        } finally {
            setIsLoading(false)
        }

        return undefined
    }

    const fetchAdmissionTypes = async (
        programId: string
    ): Promise<OptionsField[] | undefined> => {
        if (!programId) {
            return
        }

        setIsLoadingAdmissionTypes(true)
        try {
            const { data, status } =
                await resourcesApi.getSapClaseOyenteByProgramId(programId)

            if (status === 200) {
                // Mapear los sellos para cambiar "Regular" a "Primera Vez"
                const mappedData = data.map((item: OptionsField) => {
                    if (
                        item.label === 'Regular' ||
                        String(item.value) === '12'
                    ) {
                        return {
                            ...item,
                            label: 'Primera Vez'
                        }
                    }
                    return item
                })

                return mappedData
            }
        } catch {
        } finally {
            setIsLoadingAdmissionTypes(false)
        }
    }

    const fetchCampusByProgram = async (
        programId: string,
        admissionTypeId: string
    ): Promise<OptionsField[] | undefined> => {
        if (!programId || !admissionTypeId) {
            return
        }

        setIsLoadingCampus(true)
        try {
            const { data, status } = await resourcesApi.getSapCampusByProgramId(
                programId,
                admissionTypeId
            )

            if (status === 200) {
                return data
            }
        } catch {
        } finally {
            setIsLoadingCampus(false)
        }
    }

    const fetchJornada = async (
        programId: string,
        admissionTypeId: string,
        campusId: string
    ): Promise<OptionsField[] | undefined> => {
        if (!programId || !admissionTypeId || !campusId) {
            return
        }

        setIsLoadingJornada(true)
        try {
            const { data, status } =
                await resourcesApi.getSapJornadaByProgramId(
                    programId,
                    admissionTypeId,
                    campusId
                )

            if (status === 200) {
                return data
            }
        } catch {
        } finally {
            setIsLoadingJornada(false)
        }
    }

    const fetchAcademicCycleWithInitialDate = async (
        programId: number,
        admissionTypeId: string,
        headquarterId: number,
        jornadaId: number
    ): Promise<AcademicCycleWithInitialDate[] | undefined> => {
        if (!programId || !admissionTypeId || !headquarterId || !jornadaId) {
            return
        }

        setIsLoadingCycle(true)
        try {
            // Formatear jornadaId a 3 dígitos (ej: 7 -> "007", 12 -> "012")
            const formattedJornadaId = formatJornadaId(jornadaId)

            const { data, status } =
                await resourcesApi.getSapPeriodosInicioByProgramId(
                    String(programId),
                    String(admissionTypeId),
                    String(headquarterId),
                    formattedJornadaId
                )

            if (status === 200) {
                return data
            }
        } catch {
        } finally {
            setIsLoadingCycle(false)
        }
    }

    const fetchModality = async (
        programId: string,
        admissionTypeId: string,
        jornadaId: string
    ): Promise<Modality | undefined> => {
        if (!programId || !admissionTypeId || !jornadaId) {
            return
        }

        setIsLoading(true)
        try {
            // Formatear jornadaId a 3 dígitos (ej: "7" -> "007", "12" -> "012")
            const formattedJornadaId = formatJornadaId(jornadaId)

            const { data, status } = await resourcesApi.getAcademicModality(
                programId,
                admissionTypeId,
                formattedJornadaId
            )

            if (status === 200) {
                return data
            }
        } catch {
        } finally {
            setIsLoading(false)
        }
    }

    const colorChip = (
        modalityName: string
    ): 'warning' | 'primary' | 'secondary' => {
        if (modalityName.includes('resencial-virtual')) {
            return 'warning'
        }
        if (modalityName.includes('resencial')) {
            return 'primary'
        }

        return 'secondary'
    }

    return {
        fetchPrograms,
        fetchAdmissionTypes,
        fetchJornada,
        fetchAcademicCycleWithInitialDate,
        fetchCampusByProgram,
        fetchModality,
        colorChip,
        isLoading,
        isLoadingAdmissionTypes,
        isLoadingCampus,
        isLoadingJornada,
        isLoadingCycle
    }
}
